import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';

import { api, authApi } from './api';
import { useI18n } from './i18n';
import { SupportedLanguage } from './QuestionPlayer';

type AuthUser = {
  id: number;
  username: string;
  email: string;
  providers: string[];
  is_admin: boolean;
  is_premium: boolean;
  premium_until: string | null;
  account_tier: 'FREE' | 'PREMIUM' | 'ADMIN';
  is_email_verified: boolean;
  has_local_password: boolean;
  preferred_exam_category: string;
  preferred_language: SupportedLanguage;
};

type AuthTokens = {
  access: string;
  refresh: string;
};

type RegistrationResult = {
  detail: string;
  user: AuthUser;
};

type AuthContextValue = {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (identifier: string, password: string) => Promise<void>;
  loginWithGoogle: (credential: string) => Promise<void>;
  register: (username: string, email: string, password: string) => Promise<RegistrationResult>;
  resendVerification: (email: string) => Promise<string>;
  requestPasswordReset: (email: string) => Promise<string>;
  confirmPasswordReset: (uid: string, token: string, newPassword: string) => Promise<string>;
  verifyEmail: (uid: string, token: string) => Promise<string>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<string>;
  updatePreferences: (preferences: { preferredExamCategory?: string; preferredLanguage?: SupportedLanguage }) => Promise<void>;
  deleteAccount: (confirmation: string, currentPassword?: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
};

const ACCESS_TOKEN_KEY = 'pj.accessToken';
const REFRESH_TOKEN_KEY = 'pj.refreshToken';

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function readStoredTokens(): AuthTokens | null {
  const access = window.localStorage.getItem(ACCESS_TOKEN_KEY);
  const refresh = window.localStorage.getItem(REFRESH_TOKEN_KEY);

  if (!access || !refresh) {
    return null;
  }

  return { access, refresh };
}

function storeTokens(tokens: AuthTokens | null) {
  if (!tokens) {
    window.localStorage.removeItem(ACCESS_TOKEN_KEY);
    window.localStorage.removeItem(REFRESH_TOKEN_KEY);
    delete api.defaults.headers.common.Authorization;
    delete authApi.defaults.headers.common.Authorization;
    return;
  }

  window.localStorage.setItem(ACCESS_TOKEN_KEY, tokens.access);
  window.localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refresh);
  api.defaults.headers.common.Authorization = `Bearer ${tokens.access}`;
  authApi.defaults.headers.common.Authorization = `Bearer ${tokens.access}`;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { setLanguage } = useI18n();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  async function refreshUser() {
    const meResponse = await api.get('/auth/me/');
    setUser(meResponse.data);
  }

  useEffect(() => {
    const tokens = readStoredTokens();
    if (tokens) {
      api.defaults.headers.common.Authorization = `Bearer ${tokens.access}`;
      authApi.defaults.headers.common.Authorization = `Bearer ${tokens.access}`;
    }

    const requestInterceptor = api.interceptors.request.use((config) => {
      const latestTokens = readStoredTokens();
      if (latestTokens?.access) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${latestTokens.access}`;
      }
      return config;
    });

    const responseInterceptor = api.interceptors.response.use(
      (apiResponse) => apiResponse,
      async (error) => {
        const originalRequest = error.config as any;
        if (!axios.isAxiosError(error) || error.response?.status !== 401 || originalRequest?._retry) {
          return Promise.reject(error);
        }

        const latestTokens = readStoredTokens();
        if (!latestTokens?.refresh) {
          storeTokens(null);
          setUser(null);
          return Promise.reject(error);
        }

        try {
          originalRequest._retry = true;
          const refreshResponse = await authApi.post('/refresh/', { refresh: latestTokens.refresh });
          const nextTokens = {
            access: refreshResponse.data.access,
            refresh: refreshResponse.data.refresh || latestTokens.refresh,
          };
          storeTokens(nextTokens);
          authApi.defaults.headers.common.Authorization = `Bearer ${nextTokens.access}`;
          originalRequest.headers.Authorization = `Bearer ${nextTokens.access}`;
          return api(originalRequest);
        } catch (refreshError) {
          storeTokens(null);
          setUser(null);
          return Promise.reject(refreshError);
        }
      },
    );

    async function bootstrapAuth() {
      const existingTokens = readStoredTokens();
      if (!existingTokens) {
        setIsLoading(false);
        return;
      }

      try {
        await refreshUser();
      } catch (error) {
        storeTokens(null);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    }

    void bootstrapAuth();

    return () => {
      api.interceptors.request.eject(requestInterceptor);
      api.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  useEffect(() => {
    if (user?.preferred_language) {
      setLanguage(user.preferred_language);
    }
  }, [setLanguage, user?.preferred_language]);

  function applyAuthResponse(data: { access: string; refresh: string; user: AuthUser }) {
    const nextTokens = {
      access: data.access,
      refresh: data.refresh,
    };
    storeTokens(nextTokens);
    authApi.defaults.headers.common.Authorization = `Bearer ${nextTokens.access}`;
    setUser(data.user);
  }

  async function login(identifier: string, password: string) {
    const loginResponse = await authApi.post('/login/', { identifier, password });
    applyAuthResponse(loginResponse.data);
  }

  async function loginWithGoogle(credential: string) {
    const loginResponse = await authApi.post('/google/', { credential });
    applyAuthResponse(loginResponse.data);
  }

  async function register(username: string, email: string, password: string) {
    const registerResponse = await authApi.post('/register/', { username, email, password });
    return registerResponse.data as RegistrationResult;
  }

  async function resendVerification(email: string) {
    const apiResponse = await authApi.post('/resend-verification/', { email });
    return apiResponse.data.detail as string;
  }

  async function requestPasswordReset(email: string) {
    const apiResponse = await authApi.post('/password/reset/', { email });
    return apiResponse.data.detail as string;
  }

  async function confirmPasswordReset(uid: string, token: string, newPassword: string) {
    const apiResponse = await authApi.post('/password/reset/confirm/', { uid, token, new_password: newPassword });
    return apiResponse.data.detail as string;
  }

  async function verifyEmail(uid: string, token: string) {
    const apiResponse = await authApi.post('/verify-email/', { uid, token });
    return apiResponse.data.detail as string;
  }

  async function changePassword(currentPassword: string, newPassword: string) {
    const apiResponse = await authApi.post('/password/change/', {
      current_password: currentPassword,
      new_password: newPassword,
    });
    return apiResponse.data.detail as string;
  }

  async function updatePreferences(preferences: { preferredExamCategory?: string; preferredLanguage?: SupportedLanguage }) {
    const payload: Record<string, string> = {};
    if (preferences.preferredExamCategory) {
      payload.preferred_exam_category = preferences.preferredExamCategory;
    }
    if (preferences.preferredLanguage) {
      payload.preferred_language = preferences.preferredLanguage;
    }

    const apiResponse = await api.patch('/auth/me/', payload);
    setUser(apiResponse.data);
  }

  async function deleteAccount(confirmation: string, currentPassword = '') {
    const tokens = readStoredTokens();
    await authApi.post('/delete-account/', { confirmation, current_password: currentPassword });
    storeTokens(null);
    if (tokens) {
      delete authApi.defaults.headers.common.Authorization;
    }
    setUser(null);
  }

  async function logout() {
    const tokens = readStoredTokens();
    try {
      if (tokens?.refresh && user) {
        await authApi.post(
          '/logout/',
          { refresh: tokens.refresh },
          {
            headers: {
              Authorization: `Bearer ${tokens.access}`,
            },
          },
        );
      }
    } finally {
      storeTokens(null);
      delete authApi.defaults.headers.common.Authorization;
      setUser(null);
    }
  }

  const value = {
    user,
    isAuthenticated: Boolean(user),
    isLoading,
    login,
    loginWithGoogle,
    register,
    resendVerification,
    requestPasswordReset,
    confirmPasswordReset,
    verifyEmail,
    changePassword,
    updatePreferences,
    deleteAccount,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
